import React, { Fragment, useState, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import { useTranslations } from 'next-intl';
import Button from '@elements/button';
import LeftArrow from '@svg/LeftArrow';
import { useAppContext } from '@context/app';
import { IProductDetailModel, IWishlistItem } from '@types';
import { wishlistApi } from 'gate';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import TextInput from '@elements/fields/textInput';
import ImagePrepare from '@elements/imagePrepare';
import { useRouter } from 'next/router';
import { segmentEvent } from '@utils/segment';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    product?: IProductDetailModel;
    productId?: number;
    startInCreateMode?: boolean;
}

const WishlistDrawer: React.FC<Props> = ({ isOpen, onClose, product, productId, startInCreateMode }) => {
    const { user, country, area } = useAppContext();
    const t = useTranslations();
    const router = useRouter();
    const [wishlists, setWishlists] = useState<IWishlistItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showNewWishlistForm, setShowNewWishlistForm] = useState(startInCreateMode || false);
    const [selectedWishlists, setSelectedWishlists] = useState<number[] | null>(null);
    const [originalSelectedWishlists, setOriginalSelectedWishlists] = useState<number[] | null>(null);

    // Fetch user's wishlists when drawer opens, unless starting in create mode
    useEffect(() => {
        if (isOpen && user.data?.id && !startInCreateMode) {
            fetchWishlists();
        }
    }, [isOpen, user.data?.id, startInCreateMode]);

    // Effect to set showNewWishlistForm based on startInCreateMode when isOpen changes
    useEffect(() => {
        if (isOpen) {
            setShowNewWishlistForm(startInCreateMode || false);
        } else {
            // Reset when closed
            setShowNewWishlistForm(false);
        }
    }, [isOpen, startInCreateMode]);

    const fetchWishlists = async () => {
        if (!productId) return;

        setIsLoading(true);
        try {
            const response = await wishlistApi.list(productId).fetch();
            setWishlists(response.data || []);

            // Pre-select wishlists where the product is already added
            const preSelectedWishlists = response.data.filter(item => item.isSelected).map(item => item.id) || null;
            setSelectedWishlists(preSelectedWishlists);
            setOriginalSelectedWishlists(preSelectedWishlists); // Store original selection for comparison

            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching wishlists:', error);
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!productId || !selectedWishlists || isLoading) return;

        setIsLoading(true); // Set loading state for Save button
        try {
            // Add product to selected wishlist
            await wishlistApi.updateProductWishlists({
                wishlists: selectedWishlists,
                productId: productId
            }).fetch();

            // Track wishlist changes with segment events
            if (product && originalSelectedWishlists !== null) {
                const originalSet = new Set(originalSelectedWishlists);
                const newSet = new Set(selectedWishlists);

                // Find added wishlists
                const addedWishlists = selectedWishlists.filter(id => !originalSet.has(id));

                // Find removed wishlists
                const removedWishlists = originalSelectedWishlists.filter(id => !newSet.has(id));

                // Base event parameters
                const baseEventParams = {
                    area_id: area?.id || "",
                    area_slug: area?.slug || "",
                    brand_id: product.brand?.id || "",
                    brand_slug: product.brand?.slug || "",
                    category_id: product.category?.id || "",
                    category_slug: product.category?.slug || "",
                    country_id: country?.id || "",
                    country_slug: country?.slug || "",
                    default_sku_id: product.default_sku_id || "",
                    default_sku_slug: product.skus?.find(sku => sku.id === product.default_sku_id)?.slug || "",
                    environment: process.env.NODE_ENV || "",
                    from_entity: router.pathname === "/product/[slug]" ? "product" : "",
                    from_entity_slug: product?.slug || "",
                    from_page: router.pathname === "/product/[slug]" ? "product_details" :
                        router.pathname === "/wishlist" ? "wishlists" : "",
                    from_page_section: "",
                    from_page_section_entity: "",
                    from_page_section_slug: "",
                    language: router.locale?.includes("en") ? "English" : "Arabic",
                    out_of_stock: product.skus?.find(sku => sku.id === product.default_sku_id)?.buy_limit ? false : true,
                    product_id: product.id || "",
                    product_slug: product.slug || "",
                    sku_new_price: product.skus?.find(sku => sku.id === product.default_sku_id)?.price?.price || "",
                    sku_old_price: product.skus?.find(sku => sku.id === product.default_sku_id)?.price?.old_price || product.skus?.find(sku => sku.id === product.default_sku_id)?.price?.price || "",
                    subcategory_id: product.subcategory?.id || "",
                    subcategory_slug: product.subcategory?.slug || "",
                    user_id: user.data?.id || "",
                };

                // Fire event for added wishlists
                if (addedWishlists.length > 0) {
                    const addedWishlistNames = addedWishlists.map(id =>
                        wishlists.find(w => w.id === id)?.name || ""
                    );

                    segmentEvent("Wishlists Item Added", {
                        ...baseEventParams,
                        wishlist_id_list: addedWishlists.join(","),
                        wishlist_name_list: addedWishlistNames.join(","),
                    });
                }

                // Fire event for removed wishlists
                if (removedWishlists.length > 0) {
                    const removedWishlistNames = removedWishlists.map(id =>
                        wishlists.find(w => w.id === id)?.name || ""
                    );

                    segmentEvent("Wishlists Item Removed", {
                        ...baseEventParams,
                        wishlist_id_list: removedWishlists.join(","),
                        wishlist_name_list: removedWishlistNames.join(","),
                    });
                }
            }

            // Close the drawer after saving
            onClose();
        } catch (error) {
            console.error('Error saving product to wishlist:', error);
        } finally {
            setIsLoading(false); // Reset loading state for Save button
        }
    };

    const handleCreateWishlist = async (values: { name: string }) => {
        if (isLoading) return;

        setIsLoading(true); // Set loading state for Create button
        try {
            const response = await wishlistApi.create({
                name: values.name,
                product_ids: productId ? [productId] : undefined
            }).fetch();

            setShowNewWishlistForm(false);

            if (response.data?.id) {
                // Track wishlist creation with segment event
                const baseEventParams = {
                    area_id: area?.id || "",
                    area_slug: area?.slug || "",
                    country_id: country?.id || "",
                    country_slug: country?.slug || "",
                    environment: process.env.NODE_ENV || "",
                    from_entity: router.pathname === "/product/[slug]" ? "product" : "",
                    from_entity_slug: product?.slug || "",
                    from_page: router.pathname === "/product/[slug]" ? "product_details" :
                        router.pathname === "/wishlist" ? "wishlists" : "",
                    from_page_section: "",
                    from_page_section_entity: "",
                    from_page_section_slug: "",
                    language: router.locale?.includes("en") ? "English" : "Arabic",
                    user_id: user.data?.id || "",
                    wishlist_id: response.data.id || "",
                    wishlist_name: values.name || "",
                };

                if (productId && product) {
                    // Event with product
                    segmentEvent("Wishlist Created", {
                        ...baseEventParams,
                        brand_id: product.brand?.id || "",
                        brand_slug: product.brand?.slug || "",
                        category_id: product.category?.id || "",
                        category_slug: product.category?.slug || "",
                        default_sku_id: product.default_sku_id || "",
                        default_sku_slug: product.skus?.find(sku => sku.id === product.default_sku_id)?.slug || "",
                        out_of_stock: product.skus?.find(sku => sku.id === product.default_sku_id)?.buy_limit ? false : true,
                        product_id: product.id || "",
                        product_slug: product.slug || "",
                        sku_new_price: product.skus?.find(sku => sku.id === product.default_sku_id)?.price?.price || "",
                        sku_old_price: product.skus?.find(sku => sku.id === product.default_sku_id)?.price?.old_price || product.skus?.find(sku => sku.id === product.default_sku_id)?.price?.price || "",
                        subcategory_id: product.subcategory?.id || "",
                        subcategory_slug: product.subcategory?.slug || "",
                    });
                } else {
                    // Event without product
                    segmentEvent("Wishlist Created", baseEventParams);
                }

                router.push(`/wishlist/${response.data.id}`);
                onClose(); // Close the drawer after redirecting
            } else {
                // If for some reason there is no ID, refresh the list (if not adding a product)
                if (!productId) {
                    fetchWishlists();
                }
            }
        } catch (error) {
            console.error('Error creating wishlist:', error);
        } finally {
            setIsLoading(false); // Reset loading state for Create button
        }
    };

    return (
        <>
            <Transition
                show={isOpen}
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div
                    className="fixed inset-0 backdrop-brightness-50 z-[58]"
                    onClick={onClose}
                />
            </Transition>

            <Transition
                as={Fragment}
                show={isOpen}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-x-full"
                enterTo="opacity-100 translate-x-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-x-0"
                leaveTo="opacity-0 translate-x-full"
            >
                <div className="fixed right-0 top-0 h-full w-screen md:max-w-[400px] bg-stone-50 z-[59] overflow-y-auto flex flex-col">
                    {/* Header */}
                    <div className="p-4">
                        <div className="flex items-center gap-2">
                            <div onClick={showNewWishlistForm ? () => setShowNewWishlistForm(false) : onClose} className='cursor-pointer mr-3'>
                                <LeftArrow className='rtl:rotate-180' />
                            </div>
                            <h1 className="text-base font-bold text-[#4B3C2A] uppercase">
                                {showNewWishlistForm ? t("CREATE A NEW LIST") : t("CHOOSE A LIST TO SAVE ITEM")}
                            </h1>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 overflow-y-auto p-4">
                        {showNewWishlistForm ? (
                            <div className="flex flex-col h-full">
                                <Formik
                                    initialValues={{ name: '' }}
                                    validationSchema={Yup.object().shape({
                                        name: Yup.string().required(t('Name is required')),
                                    })}
                                    onSubmit={handleCreateWishlist}
                                >
                                    {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
                                        <Form className="flex flex-col h-full">
                                            <div>
                                                <TextInput
                                                    name="name"
                                                    placeholder={t('List name')}
                                                    value={values.name}
                                                    className='[&_input]:bg-white'
                                                    error={touched.name ? errors.name : undefined}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                            </div>

                                            <div className="mt-auto space-y-3 font-bold">
                                                <Button
                                                    type="submit"
                                                    className="w-full h-12 rounded-full bg-button-primary text-white"
                                                    isDisabled={isLoading}
                                                >
                                                    {t('CREATE A LIST')}
                                                </Button>

                                                <Button
                                                    type="button"
                                                    theme="lightBlue"
                                                    className="w-full h-12 rounded-full border-button-primary text-button-primary bg-transparent"
                                                    onClick={() => setShowNewWishlistForm(false)}
                                                >
                                                    {t('BACK')}
                                                </Button>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        ) : (
                            <>
                                {isLoading ? (
                                    <div className="flex justify-center items-center h-40">
                                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {wishlists.map((wishlist) => (
                                            <div
                                                key={wishlist.id}
                                                className={`flex justify-between items-center p-4 border rounded-md cursor-pointer ${selectedWishlists?.includes(wishlist.id) ? 'border-button-primary border-2' : 'border-gray-200'}`}
                                                onClick={() => setSelectedWishlists((prev) => {
                                                    if (prev) {
                                                        if (prev.includes(wishlist.id)) {
                                                            return prev.filter(id => id !== wishlist.id)
                                                        }
                                                        return [...prev, wishlist.id]
                                                    }
                                                    return [wishlist.id]
                                                })}
                                            >
                                                <div className="font-semibold text-[#4F2F18]">
                                                    {wishlist.name}
                                                </div>

                                                {/* Product Images */}
                                                <div className="flex mt-2 space-x-2">
                                                    {wishlist.products_images && wishlist.products_images.length > 0 ? (
                                                        wishlist.products_images.slice(0, 3).map((image, index) => (
                                                            <div
                                                                key={index}
                                                                className="w-16 h-16 border border-gray-50 rounded-md bg-white overflow-hidden"
                                                            >
                                                                <ImagePrepare
                                                                    media={{ url: image.image_path, type: 'image' }}
                                                                    alt=""
                                                                />
                                                            </div>
                                                        ))
                                                    ) : (

                                                        <div
                                                            className="w-16 h-16 border border-gray-200 rounded-md bg-gray-100 opacity-20"
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        ))}

                                        {wishlists.length === 0 && (
                                            <div className="text-center text-gray-500 py-6">
                                                {t('No Wishlists available')}
                                                <br />
                                                {t('Please create a wishlist to start saving your favorite products')}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Footer Buttons */}
                    {!showNewWishlistForm && (
                        <div className="p-4 mt-auto">
                            <div className="space-y-3 font-bold">
                                <Button
                                    onClick={handleSave}
                                    className="w-full h-12 rounded-full bg-button-primary text-white"
                                    isDisabled={!selectedWishlists || isLoading || wishlists.length === 0}
                                >
                                    {t('SAVE')}
                                </Button>

                                <Button
                                    theme="lightBlue"
                                    className="w-full h-12 rounded-full border-button-primary text-button-primary bg-transparent"
                                    onClick={() => setShowNewWishlistForm(true)}
                                >
                                    {t('CREATE A NEW LIST')}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </Transition>
        </>
    );
};

export default WishlistDrawer; 